// Rett kundeinformasjon og leveringsadresse på ordren.
//
// Utvikling | BMAx s. 5 pkt. 2: før en forespørsel går ut må Betongsentrum kunne
// korrigere informasjonen på prosjektet, også kundeinformasjonen. Tillegg,
// betongkvalitet og volum redigeres på «Rediger ordredetaljer»; dette er resten.
//
// Adressen er bare tekst her. Koordinater, avstand og pris regnes ikke på nytt
// — en ny adresse kan bety en annen produsent, og det byttes med «Endre
// leverandør», som regner avstanden.
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, DialogTitle, Grid } from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { notifyCenter } from "utils/toast";
import { updateOrderInfo } from "api/apiService";

const CUSTOMER = [
  ["firstName", "Fornavn"],
  ["lastName", "Etternavn"],
  ["phoneNumber", "Telefon"],
  ["emailAddress", "E-post"],
  ["companyName", "Firma"],
  ["organizationNumber", "Org.nr."],
  ["projectNumber", "Prosjektnr."],
];

const ADDRESS = [
  ["savedLocation", "Leveringsadresse (hele)"],
  ["address_1", "Gate"],
  ["postcode", "Postnr."],
  ["town", "Sted"],
];

const ERRORS = {
  invalid_email: "E-postadressen er ikke gyldig.",
  firstName_required: "Fornavn kan ikke være tomt.",
  lastName_required: "Etternavn kan ikke være tomt.",
  phoneNumber_required: "Telefon kan ikke være tomt.",
};

function OrderInfoDialog({ open, orderId, step3, address, onClose, onSaved }) {
  const [customer, setCustomer] = useState({});
  const [addr, setAddr] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCustomer(Object.fromEntries(CUSTOMER.map(([k]) => [k, step3?.[k] || ""])));
    setAddr(Object.fromEntries(ADDRESS.map(([k]) => [k, address?.[k] || ""])));
  }, [open, step3, address]);

  // Bare feltene som faktisk er endret sendes, så et felt kunden aldri fylte ut
  // ikke skrives som tom streng.
  const changed = (next, prev) =>
    Object.fromEntries(Object.entries(next).filter(([k, v]) => String(v) !== String(prev?.[k] || "")));

  const save = async () => {
    const c = changed(customer, step3);
    const a = changed(addr, address);
    if (!Object.keys(c).length && !Object.keys(a).length) {
      onClose?.();
      return;
    }
    setBusy(true);
    try {
      const res = await updateOrderInfo(orderId, {
        ...(Object.keys(c).length ? { customer: c } : {}),
        ...(Object.keys(a).length ? { address: a } : {}),
      });
      if (res?.ok) {
        notifyCenter("success", "Kundeinformasjonen er oppdatert.");
        onSaved?.();
        onClose?.();
      } else {
        notifyCenter("error", ERRORS[res?.error] || "Kunne ikke lagre endringene.");
      }
    } catch (e) {
      notifyCenter("error", "Kunne ikke lagre endringene.");
    } finally {
      setBusy(false);
    }
  };

  const field = (state, setState) => ([key, label]) => (
    <Grid item xs={12} sm={key === "savedLocation" ? 12 : 6} key={key}>
      <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
        {label}
      </SoftTypography>
      <SoftInput value={state[key] || ""} onChange={(e) => setState((p) => ({ ...p, [key]: e.target.value }))} />
    </Grid>
  );

  return (
    <Dialog open={open} onClose={() => !busy && onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>Rediger kundeinformasjon</DialogTitle>
      <DialogContent>
        <SoftTypography variant="button" fontWeight="bold" display="block" mb={1}>
          Kunde
        </SoftTypography>
        <Grid container spacing={1.5}>{CUSTOMER.map(field(customer, setCustomer))}</Grid>

        <SoftTypography variant="button" fontWeight="bold" display="block" mt={3} mb={1}>
          Leveringsadresse
        </SoftTypography>
        <Grid container spacing={1.5}>{ADDRESS.map(field(addr, setAddr))}</Grid>
        <SoftTypography variant="caption" color="text" display="block" mt={1}>
          Avstand og pris regnes ikke på nytt. Flytter leveransen til et annet område, bytt
          leverandør etterpå så avstanden blir riktig.
        </SoftTypography>

        <SoftBox mt={3} display="flex" gap={1} justifyContent="flex-end">
          <SoftButton size="small" variant="text" color="secondary" disabled={busy} onClick={onClose}>
            Avbryt
          </SoftButton>
          <SoftButton size="small" variant="gradient" color="info" disabled={busy} onClick={save}>
            {busy ? "Lagrer…" : "Lagre"}
          </SoftButton>
        </SoftBox>
      </DialogContent>
    </Dialog>
  );
}

OrderInfoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  step3: PropTypes.object,
  address: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

OrderInfoDialog.defaultProps = { step3: {}, address: {}, onSaved: null };

export default OrderInfoDialog;
